import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  CallOutlined,
  CloseRounded,
  SubjectRounded,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

const transcriptSections = [
  {
    time: "0:01",
    title: "Welcome",
    text: "Welcome to Rustic World. In this short guide, I’ll show you how to sign in, find products, add items to your shopping bag, place an order, and review your orders. You can pause or replay this video at any time.",
  },
  {
    time: "0:21",
    title: "Sign in",
    text: "To begin, select Sign in at the top of the page. Enter the email address and password connected to your Rustic World account. Then select the green Sign in button. If you do not have an account yet, select Create an account.",
  },
  {
    time: "0:41",
    title: "Browse the collection",
    text: "After signing in, the Products page opens. You can browse the complete collection, search by product name, choose a category, or enter a product number if you already know it.",
  },
  {
    time: "1:01",
    title: "Search for a product",
    text: "To find something quickly, select the search box and type a word. In this example, we search for rooster. The page immediately shows only the products that match your search.",
  },
  {
    time: "1:21",
    title: "View product details",
    text: "Select any product to view more information. Here you can see a larger photo, the price, the product number, and the available colors or styles.",
  },
  {
    time: "1:41",
    title: "Add an item to your bag",
    text: "Choose the option you prefer. Use the plus and minus buttons to set the quantity. When everything looks correct, select Add to bag. The number beside Bag at the top of the page will update.",
  },
  {
    time: "2:01",
    title: "Review your bag",
    text: "Open your bag to review your selections. Check the product, option, quantity, price, and total. You can change the quantity or remove an item before continuing.",
  },
  {
    time: "2:21",
    title: "Checkout",
    text: "When you are ready, select Checkout. A confirmation window will appear. Review the number of items and the total carefully. Select Confirm only when you are ready to place the order.",
  },
  {
    time: "2:41",
    title: "Nothing is submitted early",
    text: "You can select Cancel and return to your shopping bag at any time. Nothing is submitted until you choose Confirm.",
  },
  {
    time: "3:01",
    title: "Review your orders",
    text: "Select My orders to see your previous orders and their latest status. This is where you can return whenever you want to check an order.",
  },
  {
    time: "3:19",
    title: "Get help",
    text: "If you need assistance, select Help at the top of the page. You can also call Rustic World at 520-987-1861. A real person will be happy to help you. Thank you for visiting Rustic World.",
  },
];

export default function HelpVideoDialog({ open, onClose }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="lg"
      aria-labelledby="customer-guide-title"
      PaperProps={{
        sx: {
          bgcolor: "#FFFDF8",
          border: "1px solid #C9D3C7",
          borderRadius: { xs: 0, sm: 3 },
          boxShadow: "0 28px 80px rgba(24,36,29,.22)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ px: { xs: 2.5, sm: 4 }, pt: { xs: 2.5, sm: 3.5 }, pb: 2 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography
              id="customer-guide-title"
              component="h2"
              sx={{
                fontFamily: '"DM Serif Display", Georgia, serif',
                fontSize: { xs: 32, sm: 40 },
                lineHeight: 1.08,
                color: "#18241D",
              }}
            >
              A simple guide to shopping online
            </Typography>
            <Typography sx={{ mt: 1, color: "#39483E", fontSize: { xs: 16, sm: 18 } }}>
              3½ minutes · English narration · transcript below
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close help guide" sx={{ mt: -0.5, mr: -1 }}>
            <CloseRounded />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2.5, sm: 4 }, pb: 2 }}>
        <Box
          sx={{
            overflow: "hidden",
            borderRadius: 2.5,
            bgcolor: "#10251A",
            border: "1px solid rgba(24,36,29,.16)",
            boxShadow: "0 18px 42px rgba(24,36,29,.14)",
            width: "100%",
            maxWidth: 880,
            mx: "auto",
          }}
        >
          <Box
            component="video"
            controls
            preload="metadata"
            poster="/help/rustic-world-customer-guide-poster.jpg"
            aria-label="Video guide showing how to use the Rustic World website"
            sx={{
              width: "100%",
              aspectRatio: "16 / 9",
              display: "block",
            }}
          >
            <source src="/help/rustic-world-customer-guide.mp4?v=3" type="video/mp4" />
            Your browser does not support this video. Call Rustic World for assistance.
          </Box>
        </Box>

        <Box sx={{ mt: { xs: 3, sm: 3.5 }, maxWidth: 820 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <SubjectRounded color="primary" aria-hidden="true" />
            <Typography component="h3" sx={{ fontSize: 21, fontWeight: 800, color: "#18241D" }}>
              Video transcript
            </Typography>
          </Stack>
          <Typography sx={{ mt: 1, color: "#39483E", fontSize: 16, lineHeight: 1.55 }}>
            Read the complete instructions at your own pace.
          </Typography>
          <Box component="ol" sx={{ mt: 1.5, mb: 0, p: 0, listStyle: "none", color: "#263A2F" }}>
            {transcriptSections.map((section, index) => (
              <Box
                component="li"
                key={section.time}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "64px minmax(0, 1fr)" },
                  gap: { xs: 0.5, sm: 2 },
                  py: 2,
                  borderBottom: index === transcriptSections.length - 1 ? 0 : "1px solid #D5DED2",
                }}
              >
                <Typography
                  component="span"
                  sx={{ color: "#52675A", fontSize: 15, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}
                >
                  {section.time}
                </Typography>
                <Box>
                  <Typography component="h4" sx={{ color: "#18241D", fontSize: 18, fontWeight: 800 }}>
                    {section.title}
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: { xs: 17, sm: 18 }, lineHeight: 1.6 }}>
                    {section.text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 4 },
          py: 2.5,
          gap: 1.5,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          borderTop: "1px solid #D5DED2",
          bgcolor: "#F3F0E8",
        }}
      >
        <Button
          component={Link}
          href="tel:+15209871861"
          variant="outlined"
          startIcon={<CallOutlined />}
          sx={{ minHeight: 52, fontSize: 16, mr: { sm: "auto" } }}
        >
          Call us: (520) 987-1861
        </Button>
        <Button onClick={onClose} variant="contained" sx={{ minHeight: 52, minWidth: 140, fontSize: 16 }}>
          Close guide
        </Button>
      </DialogActions>
    </Dialog>
  );
}
